import { Row, Col, Form, Input, Button, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'types';
import { lspLoginUrl } from 'config';
import { useSetNewPassword } from 'hooks/useForgotPassword';
import { useLogin } from 'hooks/useLogin';
import { cookies } from 'static';
import { trackAnalyticsEvent, trackLoginSuccess } from 'analytics/ga4';

const getFirstSearchParamValue = (searchParams: URLSearchParams, keys: string[]) => {
  for (const key of keys) {
    const value = searchParams.get(key)?.trim();

    if (value) {
      return value;
    }
  }

  return '';
};

const isValidEmail = (value?: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

export default function InviteValidate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t }: TFunction = useTranslation();
  const [form] = Form.useForm();
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const autoContinuedTokenRef = useRef<string | null>(null);
  const { mutate: setNewPassword, isLoading: isSettingNewPassword, isError: failedSettingNewPassword } = useSetNewPassword();
  const { mutate: login, isLoading: isLoggingIn } = useLogin();

  const inviteEmail = getFirstSearchParamValue(searchParams, ['email', 'registeredEmail']);
  const inviteToken = getFirstSearchParamValue(searchParams, ['token']);
  const farmName = getFirstSearchParamValue(searchParams, ['farmName', 'farm', 'farm_name']);
  const hasLockedInviteEmail = isValidEmail(inviteEmail);
  const loginHref = lspLoginUrl || '/';

  useEffect(() => {
    trackAnalyticsEvent('reg_flow_invite_view', {
      has_invite_email: Boolean(inviteEmail),
      has_invite_token: Boolean(inviteToken),
      has_farm_name: Boolean(farmName),
    });
  }, [farmName, inviteEmail, inviteToken]);

  useEffect(() => {
    if (showSuccessScreen) {
      trackAnalyticsEvent('reg_flow_invite_show_login');
    }
  }, [showSuccessScreen]);

  const continueToCredentialsStep = useCallback(async () => {
    try {
      await form.validateFields(['token']);
      setCurrentStep(2);
    } catch {
      // Validation state is already shown by AntD form.
    }
  }, [form]);

  useEffect(() => {
    if (!inviteToken || autoContinuedTokenRef.current === inviteToken) {
      return;
    }

    autoContinuedTokenRef.current = inviteToken;
    form.setFieldsValue({ token: inviteToken });
    void continueToCredentialsStep();
  }, [continueToCredentialsStep, form, inviteToken]);

  const navigateToAppSelection = (email: string, token: string) => {
    localStorage.setItem(cookies.FarmersPlanToken, JSON.stringify({ token, username: email }));
    navigate('/app-selection', { replace: true, state: { username: email, token, forceIntroBanner: true } });
  };

  const onFinishRegistration = (values: { newPassword: string; token: string }) => {
    const emailForLogin = hasLockedInviteEmail ? inviteEmail : '';

    trackAnalyticsEvent('reg_flow_invite_submit', {
      has_email: Boolean(emailForLogin),
    });

    setNewPassword(
      { newPassword: values.newPassword, token: values.token },
      {
        onSuccess: () => {
          trackAnalyticsEvent('reg_flow_invite_password_success');

          if (!emailForLogin) {
            setShowSuccessScreen(true);
            return;
          }

          trackAnalyticsEvent('reg_flow_invite_login_submit');
          login(
            { username: emailForLogin, password: values.newPassword },
            {
              onSuccess: (data: any) => {
                trackAnalyticsEvent('reg_flow_invite_login_success');
                void trackLoginSuccess(emailForLogin);
                navigateToAppSelection(emailForLogin, data?.token);
              },
              onError: () => {
                trackAnalyticsEvent('reg_flow_invite_login_error');
                setShowSuccessScreen(true);
              },
            }
          );
        },
        onError: () => {
          trackAnalyticsEvent('reg_flow_invite_password_error');
        },
      }
    );
  };

  const onFinish = (values: { newPassword?: string; token: string }) => {
    if (currentStep === 1) {
      void continueToCredentialsStep();
      return;
    }

    if (!values.newPassword) {
      return;
    }

    onFinishRegistration({ newPassword: values.newPassword, token: values.token });
  };

  const renderInviteForm = () => (
    <Col xs={24}>
      <Typography.Title level={1} style={{ textAlign: 'center' }}>
        {t('FinishRegistration')}
      </Typography.Title>
      <Typography.Text style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
        <Trans
          i18nKey="CompleteRegistrationToAccessFarm"
          values={{ farmName: farmName || t('InviteFarmFallback') }}
          components={{ strong: <strong /> }}
        />
      </Typography.Text>
      <Form
        form={form}
        layout="vertical"
        style={{ fontSize: 18 }}
        initialValues={{ token: inviteToken }}
        onFinish={onFinish}
        validateTrigger="onChange"
      >
        {currentStep === 1 ? (
          <>
            <Form.Item
              label={t('Token')}
              name="token"
              rules={[
                {
                  required: true,
                  message: t('TokenIsRequired'),
                },
              ]}
            >
              <Input placeholder={t('Token')} size="large" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  void continueToCredentialsStep();
                }}
                style={{
                  width: '100%',
                  fontSize: 14,
                  backgroundColor: '#ff6b3d',
                  height: 46,
                }}
              >
                {t('Continue')}
              </Button>
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="token"
              hidden
              rules={[
                {
                  required: true,
                  message: t('TokenIsRequired'),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t('Email')}>
              <Typography.Text
                style={{
                  display: 'block',
                  lineHeight: '40px',
                  fontSize: 18,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {inviteEmail || '-'}
              </Typography.Text>
            </Form.Item>
            <Form.Item
              label={t('InviteValidateChoosePassword')}
              name="newPassword"
              rules={[
                {
                  required: true,
                  message: t('PasswordIsRequired'),
                },
                {
                  min: 8,
                  message: t('PasswordMustBeAtLeast8Characters'),
                },
                {
                  pattern: /^(?=.*[A-Z])(?=.*\d).{8,}$/,
                  message: t('PasswordMustContainAtLeastOneUppercaseLetterAndOneNumber'),
                },
              ]}
            >
              <Input.Password
                placeholder={t('Password')}
                size="large"
                style={{ padding: '0px 10px 0px 0px' }}
              />
            </Form.Item>
            {failedSettingNewPassword && (
              <Typography.Text style={{ display: 'block', marginBottom: 16, color: '#ff4d4f' }}>
                {t('AnErrorOccurred')}
              </Typography.Text>
            )}
            <Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={isSettingNewPassword || isLoggingIn}
                style={{
                  width: '100%',
                  fontSize: 14,
                  backgroundColor: '#ff6b3d',
                  height: 46,
                }}
              >
                {t('FinishRegistration')}
              </Button>
            </Form.Item>
            <Typography.Link
              onClick={() => setCurrentStep(1)}
              style={{ display: 'block', textAlign: 'center', marginTop: 14, color: '#999', fontSize: 14 }}
            >
              {t('Back')}
            </Typography.Link>
          </>
        )}
      </Form>
    </Col>
  );

  const renderSuccessScreen = () => (
    <Col xs={24} style={{ textAlign: 'center' }}>
      <Typography.Title level={1} style={{ textAlign: 'center' }}>
        {t('Done')}
      </Typography.Title>
      <Typography.Text style={{ textAlign: 'center' }}>
        {t('RegistrationComplete')} {t('StartByLoggingIn')}
      </Typography.Text>
      <br />
      <Button
        type="primary"
        href={loginHref}
        style={{
          textAlign: 'center',
          marginTop: 20,
          padding: 20,
          background: '#494948',
        }}
      >
        {t('Login')}
      </Button>
    </Col>
  );

  return (
    <Row
      style={{
        width: 320,
        maxWidth: '100%',
      }}
    >
      {showSuccessScreen ? renderSuccessScreen() : renderInviteForm()}
    </Row>
  );
}
